import { useEffect, useState } from 'react';

import { Alert } from 'components/shared/Alert/Alert';
import Badge from 'components/shared/Badge/Badge';
import CurrencyInput from 'components/shared/CurrencyInput/CurrencyInput';
import Disclosure from 'components/shared/Disclosure/Disclosure';
import Input from 'components/shared/Input/Input';
import Typography from 'components/shared/Typography/Typography';

import { chargeLimitAlertOptions, chargeLimitOptions } from './lib/helpers';

const ChargeLimit = ({ value, onChange }) => {
  const activeOption = value?.route_to;
  const config = value?.config ?? {};

  const percentValue = config.percent_above ?? 10;
  const [percentText, setPercentText] = useState(String(percentValue));

  useEffect(() => {
    setPercentText(String(percentValue));
  }, [percentValue]);

  const handleRouteTo = (route_to) => {
    const nextConfig =
      route_to === 'set-higher-allowable-amount' && config.percent_above === undefined
        ? { ...config, percent_above: 10 }
        : config;
    onChange({ route_to, config: nextConfig });
  };
  const handleConfig = (patch) =>
    onChange({ route_to: activeOption, config: { ...config, ...patch } });

  const handlePercentChange = (e) => {
    const raw = e.target.value;
    setPercentText(raw);
    if (raw === '') {
      handleConfig({ percent_above: 0 });
      return;
    }
    const n = Number(raw);
    if (Number.isFinite(n)) handleConfig({ percent_above: n });
  };

  const handlePercentBlur = () => {
    if (percentText === '' || !Number.isFinite(Number(percentText))) {
      setPercentText('0');
      handleConfig({ percent_above: 0 });
    }
  };

  const renderChildren = (optionValue) => {
    if (optionValue === 'set-higher-allowable-amount') {
      return (
        <div className="flex flex-col gap-4 px-1">
          <div className="flex gap-2">
            <Input
              actionButtonText="%"
              onChange={handlePercentChange}
              onBlur={handlePercentBlur}
              value={percentText}
              type="number"
              hideNumberControls
              className="max-w-32"
            />
            <Typography size="small" color="neutral-800">
              above the combined allowable amount
            </Typography>
          </div>
          <Alert
            message="e.g., 10% adds a modest buffer for patient-responsibility adjustments after insurance processes."
            icon="bulb-filled"
            variant="lighter"
            className="h-fit w-full"
          />
        </div>
      );
    }
    if (optionValue === 'set-dollar-amount') {
      return (
        <div className="flex flex-col gap-4 px-1">
          <CurrencyInput
            label="Maximum auto-charge amount"
            onValueChange={(next) => handleConfig({ dollar_cap: Number(next) || 0 })}
            value={config.dollar_cap ?? 0}
            className="max-w-[406px]"
          />
          <Alert
            message="Recommended amount: $300–$400. Amounts above this trigger an invoice."
            icon="bulb-filled"
            variant="lighter"
            className="h-fit w-full"
          />
        </div>
      );
    }
    return null;
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <Typography variant="label" size="large" weight="medium">
          Charge limit (cap)
        </Typography>
        <Typography variant="p" size="small">
          Set a limit on the amount that can be charged to a patient's card on file for incidental
          balances after treatment and/or insurance remittance.
        </Typography>
      </div>
      <div className="flex flex-col gap-2">
        {chargeLimitOptions.map((option) => {
          const isActive = option.value === activeOption;
          const children = renderChildren(option.value);
          return (
            <Disclosure
              key={option.value}
              isOpen={isActive}
              onToggle={() => handleRouteTo(option.value)}
              customTitle={
                <div className="flex gap-2">
                  <Typography variant="p" size="small" weight="medium">
                    {option.title}
                  </Typography>
                  {option.recommended && (
                    <Badge text="Recommended" variant="lighter" color="primary" size="small" />
                  )}
                </div>
              }
              disableOpen={!children}
              caption={option.caption}
              className="w-full"
              buttonClassName="py-2"
              bodyClassName="flex-col gap-0"
              radio>
              {children}
            </Disclosure>
          );
        })}
      </div>
      <Alert
        title="Patient responsibility is calculated using in-network benefits. The system pulls real-time eligibility data and applies:"
        message={
          <span className="flex flex-col gap-1 text-sm">
            {chargeLimitAlertOptions.map((item) => {
              return (
                <span
                  key={item.title}
                  className="flex gap-2 text-sm leading-[22px] text-neutral-700">
                  <span aria-hidden="true">•</span>
                  <span>
                    <span className="font-semibold">{item.title}</span>
                    {item.description && <> — {item.description}</>}
                  </span>
                </span>
              );
            })}
          </span>
        }
        icon="bulb-filled"
        variant="lighter"
        className="w-full"
      />
    </div>
  );
};

export default ChargeLimit;
